import { Alert, Flex, Spacer } from '@lokalise/louis';
import cuid from 'cuid';
import { useState } from 'react';

import { EmailTag } from '@app/components/MultiEmail';

import { AuditLogsIcon, DedicatedManagerIcon, OptionalSlaIcon, SsoIcon, VendorRateProfilesIcon } from '../Icons';
import { EnterprisePerks } from '../styles';
import { ConfirmationType } from '../types';

export const TRIAL_DAYS = 7;

const AddContributorsConfirmation = ({
    planName,
    extraSeats,
    confirmationType,
    onChargeImmediatelySeatRemoval,
    chargeImmediatelyMembers,
    chargeImmediatelyUsersCount,
}: {
    planName: string;
    extraSeats: number;
    confirmationType: ConfirmationType;
    onChargeImmediatelySeatRemoval: (emails: string) => void;
    chargeImmediatelyMembers?: string[];
    chargeImmediatelyUsersCount?: number;
}) => {
    const [emails, setEmails] = useState(chargeImmediatelyMembers);
    const [localExtraSeats, setLocalExtraSeats] = useState(extraSeats);
    const isPlural = chargeImmediatelyUsersCount && chargeImmediatelyUsersCount > 1;
    const onEmailClose = (emailToRemove: string) => {
        if (emails) {
            const newEmails = emails.filter((email) => email !== emailToRemove);
            setLocalExtraSeats(localExtraSeats - 1);
            setEmails(newEmails);
            onChargeImmediatelySeatRemoval(emailToRemove);
        }
    };
    const renderEmails = () => {
        if (emails) {
            return (
                <Flex gap={2}>
                    {emails.map((email) => (
                        <EmailTag valid key={cuid()} email={email} onClose={onEmailClose} />
                    ))}
                </Flex>
            );
        }

        return null;
    };

    switch (confirmationType) {
        case ConfirmationType.overLimit:
            return (
                <Alert status="info" role="dialog">
                    New team users being added will be free of charge until they accept your invite and their{' '}
                    {TRIAL_DAYS} day trial runs out. You can remove them at any time before that.
                    <Spacer top={3}>
                        If everyone accepts your invite, we will add{' '}
                        <strong>
                            {localExtraSeats} paid seat{localExtraSeats > 1 ? 's' : ''}
                        </strong>{' '}
                        to your plan.
                    </Spacer>
                </Alert>
            );
        case ConfirmationType.overLimitNoTrial:
            return (
                <Flex gap={3}>
                    <Alert status="info" role="dialog">
                        New team users being added will be free of charge until they accept your invite and their{' '}
                        {TRIAL_DAYS} day trial runs out. You can remove them at any time before that.{' '}
                        <Spacer top={3}>
                            If everyone accepts your invite, we will add{' '}
                            <strong>
                                {localExtraSeats} paid seat{localExtraSeats > 1 ? 's' : ''}
                            </strong>{' '}
                            to your plan.{' '}
                        </Spacer>
                        <Spacer top={3}>
                            Users listed below will take paid seats immediately since they have completed the trial
                            period.
                        </Spacer>
                    </Alert>
                    <Flex direction="column" gap={2}>
                        <strong>Users that will be added as paid immediately</strong>
                        {renderEmails()}
                    </Flex>
                </Flex>
            );

        case ConfirmationType.chargeImmediately:
            return (
                <Alert status="info" role="dialog">
                    The user you are adding has already completed the trial period and will be assigned a paid seat
                    immediately.
                </Alert>
            );

        case ConfirmationType.chargeImmediatelyNoSeats:
            return (
                <Alert status="info" role="dialog">
                    <div>
                        {isPlural ? 'Users' : 'The user'} you are adding {isPlural ? 'have' : 'has'} already used the
                        trial. Your team no longer has any available seats,{' '}
                        <strong>
                            we will add {isPlural ? `${chargeImmediatelyUsersCount} paid seats` : 'a paid seat'}{' '}
                            immediately to your subscription
                        </strong>
                        .
                    </div>
                </Alert>
            );

        case ConfirmationType.upgradeToEnterprise:
            return (
                <Alert status="info" role="dialog">
                    <div>
                        <strong>{planName} plan can have up to 50 users.</strong> In order to get more seats, you will
                        need to upgrade to the Enterprise plan.{' '}
                        <Spacer top={3}>
                            Here are some of the perks you get as an Enterprise customer:
                            <EnterprisePerks>
                                <Flex gap={[0, 2]} align="center">
                                    <DedicatedManagerIcon size="24px" /> Dedicated manager
                                </Flex>
                                <Flex gap={[0, 2]} align="center">
                                    <AuditLogsIcon size="24px" /> Audit logs
                                </Flex>
                                <Flex gap={[0, 2]} align="center">
                                    <OptionalSlaIcon size="24px" /> Optional SLA
                                </Flex>
                                <Flex gap={[0, 2]} align="center">
                                    <SsoIcon size="24px" /> SAML-based SSO
                                </Flex>
                                <Flex gap={[0, 2]} align="center">
                                    <VendorRateProfilesIcon size="24px" /> Vendor rate profiles
                                </Flex>
                            </EnterprisePerks>
                        </Spacer>
                    </div>
                </Alert>
            );

        default:
            return null;
    }
};

export default AddContributorsConfirmation;
